import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { torcidaApi, getToken, setToken, clearToken, mascararCpfInput, REGULAMENTO_URL } from './torcidaApi';
import Bandeira from './Bandeira';
import logoCentrao from '../../assets/logo-centrao.png';
import './torcida.css';

const partKey = (slug) => `torc_part_${slug}`;

// ============================================================
// Container — decide entre Identificacao e Area do participante
// ============================================================
export default function TorcidaPublico() {
  const { slug } = useParams();
  const [participante, setParticipante] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const t = getToken(slug);
    const salvo = localStorage.getItem(partKey(slug));
    if (t && salvo) {
      try { setParticipante(JSON.parse(salvo)); } catch { /* ignore */ }
    }
    setCarregando(false);
  }, [slug]);

  const aoEntrar = (p) => {
    localStorage.setItem(partKey(slug), JSON.stringify(p));
    setParticipante(p);
  };
  const sair = () => {
    clearToken(slug);
    localStorage.removeItem(partKey(slug));
    setParticipante(null);
  };

  if (carregando) {
    return <div className="torc-app"><div className="torc-loading"><div className="torc-spinner" /></div></div>;
  }

  return (
    <div className="torc-app">
      <Header />
      <div className="torc-container">
        {participante
          ? <AreaParticipante slug={slug} participante={participante} onSair={sair} />
          : <Identificacao slug={slug} onEntrar={aoEntrar} />}
      </div>
    </div>
  );
}

function Header() {
  // Logos em public/ — onError esconde graciosamente se o arquivo não existir (não quebra layout/build).
  const [amancoOk, setAmancoOk] = useState(true);
  return (
    <header className="torc-header">
      <div className="torc-logos">
        <img src={logoCentrao} alt="Centrão" className="torc-logo-centrao" />
        {amancoOk && <span className="torc-logos-div" />}
        {amancoOk && (
          <img src={`${process.env.PUBLIC_URL}/logo-amanco.webp`} alt="Amanco Wavin" className="torc-logo-amanco"
            onError={() => setAmancoOk(false)} />
        )}
      </div>
      <div className="torc-titulo-wrap">
        <span className="torc-deco">⚽</span>
        <h1 className="torc-titulo"><span className="t1">Torcida</span><span className="t2">Centrão</span></h1>
        <span className="torc-deco">🏆</span>
      </div>
      <p className="torc-tagline">Vamos torcer juntos pelo Brasil! 🇧🇷</p>
    </header>
  );
}

// ============================================================
// Tela 1 — Identificacao por CPF (+ consentimento LGPD, uma vez aqui)
// ============================================================
function Identificacao({ slug, onEntrar }) {
  const [cpf, setCpf] = useState('');
  const [aceito, setAceito] = useState(false);
  const [autoriza, setAutoriza] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const entrar = async () => {
    if (enviando) return;
    setErro('');
    const digitos = cpf.replace(/\D/g, '');
    if (digitos.length !== 11) { setErro('Digite os 11 números do seu CPF.'); return; }
    if (!aceito) { setErro('Você precisa ler e aceitar o regulamento pra participar.'); return; }
    setEnviando(true);
    try {
      const res = await torcidaApi.identificar(slug, digitos, autoriza);
      setToken(slug, res.data.token);
      onEntrar(res.data.participante);
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível entrar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="torc-card">
      <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--torc-azul)', marginTop: 0 }}>
        Palpite nos jogos do Brasil e concorra a prêmios!
      </h2>
      <label className="torc-label" htmlFor="cpf">Informe seu CPF pra participar</label>
      <input
        id="cpf" className="torc-input" inputMode="numeric" autoComplete="off"
        placeholder="000.000.000-00" value={cpf}
        onChange={(e) => setCpf(mascararCpfInput(e.target.value))}
        onKeyDown={(e) => e.key === 'Enter' && entrar()}
        maxLength={14}
      />

      {/* Aceite do regulamento — OBRIGATÓRIO pra participar (regulamento item 10.5) */}
      <div className="torc-check" style={{ marginTop: 14 }}>
        <input id="termos" type="checkbox" checked={aceito} onChange={(e) => setAceito(e.target.checked)} />
        <label htmlFor="termos">
          Li e aceito o <a href={REGULAMENTO_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--torc-azul)', fontWeight: 700 }}>regulamento da campanha</a>.
        </label>
      </div>

      {/* Consentimento LGPD — opcional */}
      <div className="torc-check">
        <input id="lgpd" type="checkbox" checked={autoriza} onChange={(e) => setAutoriza(e.target.checked)} />
        <label htmlFor="lgpd">
          Autorizo exibir meu nome no ranking público (ex: "João S."). Você concorre aos prêmios mesmo sem autorizar.
        </label>
      </div>

      <button className="torc-btn torc-btn-verde" style={{ marginTop: 6 }} onClick={entrar} disabled={enviando || !aceito}>
        {enviando ? 'Verificando...' : 'Entrar'}
      </button>
      {erro && <div className="torc-erro">{erro}</div>}
      <p className="torc-mini" style={{ marginTop: 16, marginBottom: 0 }}>
        Exclusivo para clientes cadastrados na Centrão. Não é cliente? Faça seu cadastro gratuitamente em qualquer loja.
      </p>
    </div>
  );
}

// ============================================================
// Telas 2-4 — Area do participante (boas-vindas + jogos + palpite fluido)
// ============================================================
function AreaParticipante({ slug, participante, onSair }) {
  const [jogos, setJogos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [toast, setToast] = useState('');
  const [conclusao, setConclusao] = useState(false);
  const toastTimer = useRef(null);

  const mostrarToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2500);
  };

  const carregarJogos = useCallback(async () => {
    try {
      const res = await torcidaApi.jogos(slug);
      setJogos(res.data.jogos || []);
      setResumo(res.data.resumo || null);
    } catch (e) {
      if (e.response?.status === 401) { onSair(); return; }
      setErro(e.response?.data?.error || 'Erro ao carregar os jogos.');
    } finally {
      setCarregando(false);
    }
  }, [slug, onSair]);

  useEffect(() => { carregarJogos(); }, [carregarJogos]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Palpite fluido: registra, feedback leve (toast), card vira "palpitado". Sem modal.
  const aoPalpitar = async (jogo, placarA, placarB) => {
    setErro('');
    try {
      const res = await torcidaApi.palpitar(slug, { jogo_id: jogo.id, placar_a: placarA, placar_b: placarB });
      setResumo(res.data.resumo);
      mostrarToast(`Palpite registrado: ${jogo.time_a} ${placarA} x ${placarB} ${jogo.time_b} ✓`);
      await carregarJogos();
      // Usou todos os 8 → sugere concluir/compartilhar
      if (res.data.resumo && res.data.resumo.restantes === 0) setConclusao(true);
    } catch (e) {
      mostrarToast('');
      setErro(e.response?.data?.error || 'Não foi possível registrar o palpite.');
      setTimeout(() => setErro(''), 5000);
    }
  };

  const jogosBrasil = useMemo(() => jogos.filter(j => j.eh_jogo_do_brasil), [jogos]);
  const jogosOutros = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return jogos.filter(j => !j.eh_jogo_do_brasil)
      .filter(j => !termo || j.time_a.toLowerCase().includes(termo) || j.time_b.toLowerCase().includes(termo));
  }, [jogos, busca]);

  if (carregando) return <div className="torc-loading"><div className="torc-spinner" /></div>;

  const livresDisp = resumo ? resumo.livres_max - resumo.livres_usados : 5;
  const algumPalpite = resumo && resumo.total_usados > 0;

  return (
    <>
      {/* Toast de feedback */}
      {toast && <div className="torc-toast">{toast}</div>}

      {/* Tela 2 — Boas-vindas */}
      <div className="torc-card torc-card-amarelo">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800 }}>Olá, {participante.primeiro_nome}! 🎯</h2>
            <p style={{ margin: '4px 0 0', fontWeight: 700 }}>Loja: {participante.loja?.nome}</p>
            {participante.eh_funcionario && (
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', fontWeight: 700, color: 'var(--torc-azul)' }}>
                🛠️ Funcionário — você concorre no ranking de funcionários
              </p>
            )}
          </div>
          <button className="torc-btn-link" style={{ color: 'var(--torc-azul)' }} onClick={onSair}>Sair</button>
        </div>
        {resumo && (
          <div className="torc-resumo">
            <div className="torc-resumo-item">
              <div className="torc-resumo-num">{resumo.obrigatorios_usados}/{resumo.obrigatorios_max}</div>
              <div className="torc-resumo-lbl">Jogos do Brasil 🇧🇷</div>
            </div>
            <div className="torc-resumo-item">
              <div className="torc-resumo-num">{resumo.livres_usados}/{resumo.livres_max}</div>
              <div className="torc-resumo-lbl">Palpites livres</div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {algumPalpite && (
            <button className="torc-btn torc-btn-verde" onClick={() => setConclusao(true)}>✓ Concluir</button>
          )}
          <Link to={`/torcida/${slug}/ranking`} className="torc-btn torc-btn-azul" style={{ textDecoration: 'none' }}>
            🏆 Ranking
          </Link>
        </div>
      </div>

      {erro && <div className="torc-erro">{erro}</div>}

      {/* Tela 3 — Jogos do Brasil (obrigatorios) */}
      {jogosBrasil.length > 0 && (
        <div className="torc-card">
          <h3 className="torc-secao torc-secao-brasil">
            🇧🇷 Jogos do Brasil <small>palpites obrigatórios</small>
          </h3>
          {jogosBrasil.map(j => <CardJogo key={j.id} jogo={j} onPalpitar={aoPalpitar} />)}
        </div>
      )}

      {/* Tela 3 — Outros jogos (livres) */}
      <div className="torc-card">
        <h3 className="torc-secao torc-secao-livre">
          Outros jogos <small>{livresDisp} de {resumo?.livres_max ?? 5} palpites livres</small>
        </h3>
        <input
          className="torc-input" placeholder="🔎 Buscar uma seleção ou jogo..."
          value={busca} onChange={(e) => setBusca(e.target.value)} style={{ marginBottom: 14, fontSize: '0.95rem' }}
        />
        {jogosOutros.length === 0 && <p className="torc-mini">Nenhum jogo encontrado.</p>}
        {jogosOutros.map(j => <CardJogo key={j.id} jogo={j} onPalpitar={aoPalpitar} semPalpitesLivres={livresDisp <= 0} />)}
      </div>

      {/* Botao salvar/concluir sempre acessivel no rodape */}
      <button className="torc-btn torc-btn-amarelo" onClick={() => setConclusao(true)}>
        💾 Salvar e concluir meus palpites
      </button>

      {/* Tela final — conclusao + compartilhar (so no fim) */}
      {conclusao && (
        <ModalConclusao resumo={resumo} nome={participante.primeiro_nome} slug={slug}
          onFechar={() => setConclusao(false)} />
      )}
    </>
  );
}

// ============================================================
// Card de jogo — palpite inline fluido, auto-advance no placar
// ============================================================
function CardJogo({ jogo, onPalpitar, semPalpitesLivres }) {
  const jaPalpitou = !!jogo.meu_palpite;
  const [a, setA] = useState(jogo.meu_palpite ? String(jogo.meu_palpite.placar_a) : '');
  const [b, setB] = useState(jogo.meu_palpite ? String(jogo.meu_palpite.placar_b) : '');
  const [enviando, setEnviando] = useState(false);
  const refB = useRef(null);
  const wrapRef = useRef(null);

  const bloqueadoLivre = !jaPalpitou && !jogo.eh_jogo_do_brasil && semPalpitesLivres;
  const podePalpitar = !jaPalpitou && !jogo.bloqueado && !bloqueadoLivre;

  const data = new Date(jogo.data_hora);
  const dataFmt = data.toLocaleString('pt-BR', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  // Auto-advance: ao digitar 1 digito no placar A, foca no placar B (placar de futebol e quase sempre 1 digito)
  const onChangeA = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 1);
    setA(d);
    if (d) refB.current?.focus();
  };
  const onChangeB = (v) => setB(v.replace(/\D/g, '').slice(0, 1));

  const salvar = async (na, nb) => {
    if (!Number.isInteger(na) || !Number.isInteger(nb) || enviando || jaPalpitou) return;
    setEnviando(true);
    await onPalpitar(jogo, na, nb);
    setEnviando(false);
  };

  // Salva automaticamente quando o foco sai dos DOIS campos do placar (tocou/clicou fora),
  // com ambos preenchidos. Trocar entre A<->B não salva. Resolve o caso de esquecer de confirmar.
  const aoSairDoPlacar = () => {
    setTimeout(() => {
      if (wrapRef.current && wrapRef.current.contains(document.activeElement)) return; // foco ainda nos campos
      const na = parseInt(a, 10), nb = parseInt(b, 10);
      if (Number.isInteger(na) && Number.isInteger(nb)) salvar(na, nb);
    }, 120);
  };

  return (
    <div className={`torc-jogo ${jogo.eh_jogo_do_brasil ? 'brasil' : ''} ${jaPalpitou ? 'palpitado' : ''} ${jogo.bloqueado ? 'bloqueado' : ''}`}>
      <div style={{ textAlign: 'center' }}>
        <span className={`torc-jogo-tag ${jogo.eh_jogo_do_brasil ? 'torc-tag-obrig' : 'torc-tag-livre'}`}>
          {jogo.eh_jogo_do_brasil ? 'Palpite obrigatório' : 'Palpite livre'}
        </span>
      </div>
      <div className="torc-jogo-data">{dataFmt}</div>
      <div className="torc-jogo-times">
        <div className="torc-time">
          <Bandeira codigo={jogo.time_a_codigo} nome={jogo.time_a} />
          <span className="torc-time-nome">{jogo.time_a}</span>
        </div>
        {jaPalpitou || jogo.finalizado ? (
          <PlacarFixo jogo={jogo} />
        ) : podePalpitar ? (
          <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="torc-placar-input" inputMode="numeric" maxLength={1} value={a}
              onChange={(e) => onChangeA(e.target.value)} onBlur={aoSairDoPlacar} disabled={enviando}
              aria-label={`Placar ${jogo.time_a}`} />
            <span className="torc-vs">x</span>
            <input ref={refB} className="torc-placar-input" inputMode="numeric" maxLength={1} value={b}
              onChange={(e) => onChangeB(e.target.value)} onBlur={aoSairDoPlacar} disabled={enviando}
              onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} aria-label={`Placar ${jogo.time_b}`} />
          </div>
        ) : (
          <span className="torc-vs">x</span>
        )}
        <div className="torc-time">
          <Bandeira codigo={jogo.time_b_codigo} nome={jogo.time_b} />
          <span className="torc-time-nome">{jogo.time_b}</span>
        </div>
      </div>

      {jogo.finalizado && jaPalpitou ? (
        <div className="torc-sucesso-msg" style={{ textAlign: 'center' }}>
          Seu palpite: {jogo.meu_palpite.placar_a} x {jogo.meu_palpite.placar_b}
          {jogo.meu_palpite.pontos != null && ` — ${jogo.meu_palpite.pontos} ponto(s) 🎉`}
        </div>
      ) : jaPalpitou ? null
      : jogo.bloqueado ? (
        <p className="torc-mini" style={{ textAlign: 'center', margin: 0 }}>⏱️ Palpites encerrados (1h antes do jogo)</p>
      ) : bloqueadoLivre ? (
        <p className="torc-mini" style={{ textAlign: 'center', margin: 0 }}>Você já usou seus 5 palpites livres.</p>
      ) : enviando ? (
        <p className="torc-mini" style={{ textAlign: 'center', margin: 0, color: 'var(--torc-verde)', fontWeight: 700 }}>💾 Salvando palpite...</p>
      ) : null}
    </div>
  );
}

function PlacarFixo({ jogo }) {
  if (jogo.finalizado && jogo.placar) {
    return <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--torc-azul)', lineHeight: 1, padding: '0 6px' }}>{jogo.placar.a} x {jogo.placar.b}</div>;
  }
  if (jogo.meu_palpite) {
    return <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--torc-verde)', lineHeight: 1, padding: '0 6px' }}>{jogo.meu_palpite.placar_a} x {jogo.meu_palpite.placar_b}</div>;
  }
  return <span className="torc-vs">x</span>;
}

// ============================================================
// Tela final — conclusao + compartilhar WhatsApp (so no fim)
// ============================================================
function ModalConclusao({ resumo, nome, slug, onFechar }) {
  const usados = resumo ? resumo.total_usados : 0;
  const texto = `Já fiz meus palpites na Torcida Centrão pros jogos do Brasil! 🇧🇷⚽ ` +
    `Vamos torcer juntos! Participe você também na sua loja Centrão.`;
  const linkWpp = `https://wa.me/?text=${encodeURIComponent(texto)}`;

  return (
    <div className="torc-overlay" onMouseDown={onFechar}>
      <div className="torc-modal" onMouseDown={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.6rem' }}>🇧🇷</div>
        <h3 style={{ margin: '6px 0', color: 'var(--torc-verde)' }}>Seus palpites estão salvos!</h3>
        <p style={{ margin: '4px 0' }}>Valeu, {nome}! Você fez <strong>{usados}</strong> palpite(s).</p>
        <p className="torc-mini">Boa sorte! Acompanhe o ranking da sua loja e torça pelo Brasil. 🟢🟡</p>
        <a className="torc-btn torc-btn-wpp" href={linkWpp} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', marginTop: 10 }}>
          📲 Chamar a galera no WhatsApp
        </a>
        <Link to={`/torcida/${slug}/ranking`} className="torc-btn torc-btn-azul" style={{ textDecoration: 'none', marginTop: 8 }}>
          🏆 Ver ranking da minha loja
        </Link>
        <button className="torc-btn-link" style={{ width: '100%', marginTop: 6 }} onClick={onFechar}>
          {resumo && resumo.restantes > 0 ? 'Continuar palpitando' : 'Fechar'}
        </button>
      </div>
    </div>
  );
}
